// features/inventory/pages/NotificationPreferencesPage.tsx
/**
 * Notification Preferences Page — Configure stock alert thresholds and notification settings.
 *
 * Route: /inventory/notifications
 *
 * Features:
 * - Global low stock threshold configuration
 * - Per-product threshold override with search
 * - Notification method toggles (in-app, email, push)
 * - Alert frequency settings
 * - Quick threshold presets
 * - Bulk threshold update
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { getProducts, updateProduct, ProductData } from '@/features/products/services/productsApi';
import { getBranches } from '@/features/settings/services/settingsApi';
import {
    ArrowLeft,
    Save,
    Bell,
    Search,
    X,
    Package,
    Store,
    Mail,
    Smartphone,
    Monitor,
    RefreshCw,
    Clock,
    Calendar,
    Zap,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

interface NotificationSettings {
    inAppAlerts: boolean;
    emailAlerts: boolean;
    pushAlerts: boolean;
    alertFrequency: 'instant' | 'hourly' | 'daily' | 'weekly';
    defaultLowStockThreshold: number;
    defaultReorderPoint: number;
    defaultReorderQuantity: number;
    notifyOnOutOfStock: boolean;
    notifyOnConfirmed: boolean;
    digestTime: string;
}

interface ProductThresholdOverride {
    productId: string;
    productName: string;
    sku: string;
    currentThreshold: number;
    newThreshold: number;
    isModified: boolean;
}

// ============================================================
// CONSTANTS
// ============================================================

const THRESHOLD_PRESETS = [5, 10, 20, 50, 100];
const PAGE_SIZE = 25;

const FREQUENCY_OPTIONS = [
    {
        value: 'instant',
        label: 'Instant',
        icon: Zap,
        desc: 'Alert immediately when stock drops below threshold',
    },
    {
        value: 'hourly',
        label: 'Hourly Digest',
        icon: Clock,
        desc: 'Send a summary every hour',
    },
    {
        value: 'daily',
        label: 'Daily Digest',
        icon: Calendar,
        desc: 'One summary per day',
    },
    {
        value: 'weekly',
        label: 'Weekly Report',
        icon: Calendar,
        desc: 'Weekly stock status report',
    },
] as const;

// ============================================================
// MAIN COMPONENT
// ============================================================

export function NotificationPreferencesPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    // State
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [activeTab, setActiveTab] = useState<'general' | 'products'>('general');
    const [hasChanges, setHasChanges] = useState(false);

    // Notification settings
    const [settings, setSettings] = useState<NotificationSettings>({
        inAppAlerts: true,
        emailAlerts: false,
        pushAlerts: false,
        alertFrequency: 'instant',
        defaultLowStockThreshold: 10,
        defaultReorderPoint: 20,
        defaultReorderQuantity: 50,
        notifyOnOutOfStock: true,
        notifyOnConfirmed: false,
        digestTime: '08:00',
    });

    // Product thresholds
    const [productThresholds, setProductThresholds] = useState<ProductThresholdOverride[]>([]);
    const [bulkThreshold, setBulkThreshold] = useState('');

    // Queries
    const { data: branches } = useQuery({
        queryKey: ['branches-notifications', user?.tenantId],
        queryFn: async () => {
            try {
                const data = await getBranches(user!.tenantId);
                return Array.isArray(data) ? data : [];
            } catch {
                return [];
            }
        },
        enabled: !!user?.tenantId,
    });

    const { data: productsResponse, isLoading: productsLoading } = useQuery({
        queryKey: ['products-thresholds', user?.tenantId, search, page],
        queryFn: async () => {
            const response = await getProducts(user!.tenantId, {
                limit: PAGE_SIZE,
                page,
                search: search || undefined,
                sortBy: 'name',
                order: 'asc',
            });
            return response;
        },
        enabled: !!user?.tenantId && activeTab === 'products',
    });

    // Derived data
    const branchesList = (branches || []) as Array<{ id: string; name: string }>;
    const products = productsResponse?.data || [];
    const totalProducts = productsResponse?.meta?.totalCount || 0;
    const totalPages = Math.max(1, Math.ceil(totalProducts / PAGE_SIZE));

    // Sync product data to threshold overrides when products load
    useState(() => {
        if (products.length > 0 && productThresholds.length === 0) {
            const thresholds = products.map((p: ProductData) => ({
                productId: p.id,
                productName: p.name,
                sku: p.sku || '',
                currentThreshold: p.lowStockThreshold || 10,
                newThreshold: p.lowStockThreshold || 10,
                isModified: false,
            }));
            setProductThresholds(thresholds);
        }
    });

    // Mutations
    const saveSettingsMutation = useMutation({
        mutationFn: async () => {
            const modifiedThresholds = productThresholds.filter((t) => t.isModified);
            for (const threshold of modifiedThresholds) {
                await updateProduct(user!.tenantId, threshold.productId, {
                    lowStockThreshold: threshold.newThreshold,
                    reorderPoint: settings.defaultReorderPoint,
                    reorderQuantity: settings.defaultReorderQuantity,
                });
            }
        },
        onSuccess: () => {
            addToast({
                type: 'success',
                title: 'Settings saved',
                message: `${productThresholds.filter((t) => t.isModified).length} products updated.`,
            });
            setHasChanges(false);
            setProductThresholds((prev) =>
                prev.map((t) => ({
                    ...t,
                    currentThreshold: t.newThreshold,
                    isModified: false,
                }))
            );
            queryClient.invalidateQueries({ queryKey: ['products'] });
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Save failed',
                message: err?.message,
            });
        },
    });

    // Handlers
    const handleSettingChange = useCallback(
        (key: keyof NotificationSettings, value: string | number | boolean) => {
            setSettings((prev) => ({ ...prev, [key]: value }));
            setHasChanges(true);
        },
        []
    );

    const handleThresholdChange = useCallback(
        (productId: string, value: number) => {
            setProductThresholds((prev) =>
                prev.map((t) =>
                    t.productId === productId
                        ? {
                            ...t,
                            newThreshold: value,
                            isModified: value !== t.currentThreshold,
                        }
                        : t
                )
            );
            setHasChanges(true);
        },
        []
    );

    const handleBulkApply = useCallback(() => {
        const value = parseInt(bulkThreshold);
        if (isNaN(value) || value < 0) return;
        setProductThresholds((prev) =>
            prev.map((t) => ({
                ...t,
                newThreshold: value,
                isModified: value !== t.currentThreshold,
            }))
        );
        setHasChanges(true);
        setBulkThreshold('');
        addToast({
            type: 'success',
            title: 'Bulk update applied',
            message: `${productThresholds.length} products set to threshold: ${value}`,
        });
    }, [bulkThreshold, productThresholds, addToast]);

    const handlePresetClick = useCallback(
        (value: number) => {
            setBulkThreshold(value.toString());
            setProductThresholds((prev) =>
                prev.map((t) => ({
                    ...t,
                    newThreshold: value,
                    isModified: value !== t.currentThreshold,
                }))
            );
            setHasChanges(true);
        },
        []
    );

    const handlePageChange = useCallback((newPage: number) => {
        if (newPage < 1 || newPage > totalPages || newPage === page) return;
        setPage(newPage);
    }, [totalPages, page]);

    const modifiedCount = productThresholds.filter((t) => t.isModified).length;

    // ============================================================
    // RENDER
    // ============================================================

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/inventory')}
                                className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                                type="button"
                            >
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl shadow-lg shadow-amber-500/25">
                                <Bell className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">
                                    Notification Preferences
                                </h1>
                                <p className="text-sm text-slate-500">
                                    Configure stock alerts and thresholds
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {hasChanges && (
                                <button
                                    onClick={() => saveSettingsMutation.mutate()}
                                    disabled={saveSettingsMutation.isPending}
                                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 transition-all font-semibold shadow-lg text-sm"
                                    type="button"
                                >
                                    {saveSettingsMutation.isPending ? (
                                        <Spinner size="xs" variant="white" />
                                    ) : (
                                        <Save className="w-4 h-4" />
                                    )}
                                    Save Changes
                                    {modifiedCount > 0 ? ` (${modifiedCount})` : ''}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1400px] mx-auto p-6 lg:p-8 space-y-6">
                {/* Tabs */}
                <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-0.5 w-fit">
                    <button
                        onClick={() => setActiveTab('general')}
                        className={clsx(
                            'flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all',
                            activeTab === 'general'
                                ? 'bg-white text-amber-600 shadow-sm'
                                : 'text-slate-500 hover:text-slate-700'
                        )}
                        type="button"
                    >
                        <Bell className="w-4 h-4" />
                        General Settings
                    </button>
                    <button
                        onClick={() => setActiveTab('products')}
                        className={clsx(
                            'flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all',
                            activeTab === 'products'
                                ? 'bg-white text-amber-600 shadow-sm'
                                : 'text-slate-500 hover:text-slate-700'
                        )}
                        type="button"
                    >
                        <Package className="w-4 h-4" />
                        Product Thresholds
                    </button>
                </div>

                {/* GENERAL SETTINGS TAB */}
                {activeTab === 'general' && (
                    <div className="space-y-6">
                        {/* Alert Methods */}
                        <Card>
                            <CardContent className="p-6 space-y-4">
                                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                                    <Bell className="w-5 h-5 text-amber-600" />
                                    Alert Methods
                                </h3>
                                <p className="text-xs text-slate-500 -mt-3">
                                    Choose how you want to receive stock alerts
                                </p>

                                <div className="space-y-3">
                                    {([
                                        {
                                            key: 'inAppAlerts' as keyof NotificationSettings,
                                            label: 'In-App Notifications',
                                            desc: 'Show alerts in the dashboard and sidebar',
                                            icon: Monitor,
                                        },
                                        {
                                            key: 'emailAlerts' as keyof NotificationSettings,
                                            label: 'Email Alerts',
                                            desc: 'Send alert emails to registered email',
                                            icon: Mail,
                                        },
                                        {
                                            key: 'pushAlerts' as keyof NotificationSettings,
                                            label: 'Push Notifications',
                                            desc: 'Send push notifications to mobile devices',
                                            icon: Smartphone,
                                        },
                                    ] as Array<{
                                        key: keyof NotificationSettings;
                                        label: string;
                                        desc: string;
                                        icon: React.ElementType;
                                    }>).map((method) => (
                                        <div
                                            key={method.key}
                                            className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 rounded-lg bg-white">
                                                    <method.icon className="w-5 h-5 text-slate-600" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-semibold text-slate-700">
                                                        {method.label}
                                                    </p>
                                                    <p className="text-xs text-slate-400">
                                                        {method.desc}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() =>
                                                    handleSettingChange(
                                                        method.key,
                                                        !settings[method.key]
                                                    )
                                                }
                                                className={clsx(
                                                    'relative w-14 h-8 rounded-full transition-colors duration-300 flex-shrink-0',
                                                    settings[method.key]
                                                        ? 'bg-emerald-500'
                                                        : 'bg-slate-300'
                                                )}
                                                type="button"
                                                role="switch"
                                                aria-checked={
                                                    settings[method.key] ? 'true' : 'false'
                                                }
                                            >
                                                <motion.div
                                                    className="absolute top-0.5 w-7 h-7 bg-white rounded-full shadow-md"
                                                    animate={{
                                                        left: settings[method.key]
                                                            ? 'calc(100% - 1.875rem)'
                                                            : '0.125rem',
                                                    }}
                                                    transition={{
                                                        type: 'spring',
                                                        stiffness: 500,
                                                        damping: 30,
                                                    }}
                                                />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Alert Frequency */}
                        <Card>
                            <CardContent className="p-6 space-y-4">
                                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                                    <Clock className="w-5 h-5 text-amber-600" />
                                    Alert Frequency
                                </h3>
                                <p className="text-xs text-slate-500 -mt-3">
                                    How often should alerts be sent?
                                </p>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                    {FREQUENCY_OPTIONS.map((option) => (
                                        <button
                                            key={option.value}
                                            onClick={() =>
                                                handleSettingChange(
                                                    'alertFrequency',
                                                    option.value
                                                )
                                            }
                                            className={clsx(
                                                'p-4 rounded-xl border-2 text-left transition-all',
                                                settings.alertFrequency === option.value
                                                    ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-500/20'
                                                    : 'border-slate-200 hover:border-amber-300'
                                            )}
                                            type="button"
                                        >
                                            <option.icon
                                                className={clsx(
                                                    'w-5 h-5 mb-2',
                                                    settings.alertFrequency === option.value
                                                        ? 'text-amber-600'
                                                        : 'text-slate-400'
                                                )}
                                            />
                                            <p className="font-semibold text-sm">
                                                {option.label}
                                            </p>
                                            <p className="text-[10px] text-slate-400 mt-1">
                                                {option.desc}
                                            </p>
                                        </button>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Default Thresholds */}
                        <Card>
                            <CardContent className="p-6 space-y-4">
                                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                                    <Package className="w-5 h-5 text-amber-600" />
                                    Default Thresholds
                                </h3>
                                <p className="text-xs text-slate-500 -mt-3">
                                    These apply to all products unless overridden
                                </p>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                            Low Stock Threshold
                                        </label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={settings.defaultLowStockThreshold}
                                            onChange={(e) =>
                                                handleSettingChange(
                                                    'defaultLowStockThreshold',
                                                    parseInt(e.target.value) || 0
                                                )
                                            }
                                            className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400"
                                        />
                                        <p className="text-[10px] text-slate-400 mt-1">
                                            Alert when stock drops below this number
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                            Reorder Point
                                        </label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={settings.defaultReorderPoint}
                                            onChange={(e) =>
                                                handleSettingChange(
                                                    'defaultReorderPoint',
                                                    parseInt(e.target.value) || 0
                                                )
                                            }
                                            className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400"
                                        />
                                        <p className="text-[10px] text-slate-400 mt-1">
                                            Suggested reorder trigger point
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                            Reorder Quantity
                                        </label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={settings.defaultReorderQuantity}
                                            onChange={(e) =>
                                                handleSettingChange(
                                                    'defaultReorderQuantity',
                                                    parseInt(e.target.value) || 0
                                                )
                                            }
                                            className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400"
                                        />
                                        <p className="text-[10px] text-slate-400 mt-1">
                                            Default quantity to reorder
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Additional Settings */}
                        <Card>
                            <CardContent className="p-6 space-y-3">
                                <h3 className="font-semibold text-slate-800">
                                    Additional Notifications
                                </h3>

                                {([
                                    {
                                        key: 'notifyOnOutOfStock' as keyof NotificationSettings,
                                        label: 'Out of Stock Alerts',
                                        desc: 'Notify when a product reaches zero stock',
                                    },
                                    {
                                        key: 'notifyOnConfirmed' as keyof NotificationSettings,
                                        label: 'Confirmation Alerts',
                                        desc: 'Notify when stock has been reviewed and confirmed',
                                    },
                                ] as Array<{
                                    key: keyof NotificationSettings;
                                    label: string;
                                    desc: string;
                                }>).map((item) => (
                                    <div
                                        key={item.key}
                                        className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                                    >
                                        <div>
                                            <p className="text-sm font-semibold text-slate-700">
                                                {item.label}
                                            </p>
                                            <p className="text-xs text-slate-400">
                                                {item.desc}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() =>
                                                handleSettingChange(
                                                    item.key,
                                                    !settings[item.key]
                                                )
                                            }
                                            className={clsx(
                                                'relative w-14 h-8 rounded-full transition-colors duration-300 flex-shrink-0',
                                                settings[item.key]
                                                    ? 'bg-emerald-500'
                                                    : 'bg-slate-300'
                                            )}
                                            type="button"
                                            role="switch"
                                            aria-checked={
                                                settings[item.key] ? 'true' : 'false'
                                            }
                                        >
                                            <motion.div
                                                className="absolute top-0.5 w-7 h-7 bg-white rounded-full shadow-md"
                                                animate={{
                                                    left: settings[item.key]
                                                        ? 'calc(100% - 1.875rem)'
                                                        : '0.125rem',
                                                }}
                                                transition={{
                                                    type: 'spring',
                                                    stiffness: 500,
                                                    damping: 30,
                                                }}
                                            />
                                        </button>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* PRODUCT THRESHOLDS TAB */}
                {activeTab === 'products' && (
                    <div className="space-y-4">
                        {/* Bulk Update Bar */}
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3 flex-wrap">
                                    <p className="text-sm font-semibold text-slate-700">
                                        Quick Presets:
                                    </p>
                                    {THRESHOLD_PRESETS.map((value) => (
                                        <button
                                            key={value}
                                            onClick={() => handlePresetClick(value)}
                                            className="px-3 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
                                            type="button"
                                        >
                                            {value}
                                        </button>
                                    ))}
                                    <div className="flex items-center gap-2 ml-4">
                                        <input
                                            type="number"
                                            min="0"
                                            value={bulkThreshold}
                                            onChange={(e) =>
                                                setBulkThreshold(e.target.value)
                                            }
                                            placeholder="Custom"
                                            className="w-24 px-3 py-1.5 border-2 border-slate-200 rounded-lg text-sm"
                                        />
                                        <button
                                            onClick={handleBulkApply}
                                            disabled={!bulkThreshold}
                                            className="px-4 py-1.5 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors disabled:opacity-50"
                                            type="button"
                                        >
                                            Apply to All
                                        </button>
                                    </div>
                                    {modifiedCount > 0 && (
                                        <Badge variant="warning" size="sm">
                                            {modifiedCount} modified
                                        </Badge>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search products to configure thresholds..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full pl-12 pr-12 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 text-sm shadow-sm"
                            />
                            {search && (
                                <button
                                    onClick={() => {
                                        setSearch('');
                                        setPage(1);
                                    }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-slate-400 hover:text-slate-600"
                                    type="button"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>

                        {/* Products Table */}
                        {productsLoading ? (
                            <div className="flex justify-center py-20">
                                <Spinner size="xl" />
                            </div>
                        ) : products.length === 0 ? (
                            <EmptyState
                                icon={<Package className="w-16 h-16" />}
                                title="No products found"
                                description={
                                    search
                                        ? 'Try a different search term.'
                                        : 'Add products first.'
                                }
                            />
                        ) : (
                            <Card className="overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-slate-100">
                                        <thead>
                                            <tr className="bg-slate-50/50">
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                                                    Product
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                                                    SKU
                                                </th>
                                                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-32">
                                                    Current Threshold
                                                </th>
                                                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-32">
                                                    New Threshold
                                                </th>
                                                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-24">
                                                    Status
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {productThresholds.map((item) => (
                                                <tr
                                                    key={item.productId}
                                                    className={clsx(
                                                        'hover:bg-slate-50 transition-colors',
                                                        item.isModified &&
                                                        'bg-amber-50/30'
                                                    )}
                                                >
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center gap-2">
                                                            <Package className="w-4 h-4 text-slate-400" />
                                                            <span className="text-sm font-medium text-slate-700">
                                                                {
                                                                    item.productName
                                                                }
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-slate-500 font-mono">
                                                        {item.sku ||
                                                            '—'}
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <span className="text-sm font-semibold text-slate-600">
                                                            {
                                                                item.currentThreshold
                                                            }
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={
                                                                item.newThreshold
                                                            }
                                                            onChange={(e) =>
                                                                handleThresholdChange(
                                                                    item.productId,
                                                                    parseInt(
                                                                        e
                                                                            .target
                                                                            .value
                                                                    ) || 0
                                                                )
                                                            }
                                                            className={clsx(
                                                                'w-20 px-2 py-1.5 border-2 rounded-lg text-sm text-center font-semibold',
                                                                item.isModified
                                                                    ? 'border-amber-400 bg-amber-50 text-amber-700'
                                                                    : 'border-slate-200 text-slate-700'
                                                            )}
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        {item.isModified ? (
                                                            <Badge
                                                                variant="warning"
                                                                size="sm"
                                                            >
                                                                Modified
                                                            </Badge>
                                                        ) : (
                                                            <Badge
                                                                variant="success"
                                                                size="sm"
                                                            >
                                                                Default
                                                            </Badge>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {totalPages > 1 && (
                                    <Pagination
                                        currentPage={page}
                                        totalPages={totalPages}
                                        totalItems={totalProducts}
                                        pageSize={PAGE_SIZE}
                                        onPageChange={handlePageChange}
                                        onPageSizeChange={() => {
                                            setPage(1);
                                        }}
                                    />
                                )}
                            </Card>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}